/**
 * useClientSparklineData Hook
 *
 * Transforms the `monthlyByClient` data from getClientMatrix tRPC response
 * into a Map for efficient lookup when rendering sparklines in the table.
 *
 * @see docs/stories/DRE-CLIENT-V2-story-4.003.md
 */

import { useMemo } from 'react'

/** Monthly revenue data point */
export interface MonthlyRevenuePoint {
  period: string
  receita: number
}

/** Per-client monthly data from backend */
export interface ClientMonthlyData {
  codigoCliente: string
  months: MonthlyRevenuePoint[]
}

/** Return type of the hook */
export interface UseClientSparklineDataReturn {
  /** Map from codigoCliente to array of monthly revenue values (chronologically sorted) */
  sparklineData: Map<string, number[]>
  /** Check if a client has sparkline data */
  hasData: (codigoCliente: string) => boolean
  /** Get sparkline data for a specific client */
  getData: (codigoCliente: string) => number[]
}

/**
 * Hook to transform monthly client data into sparkline-ready format
 *
 * @param monthlyByClient - Array of per-client monthly data from backend
 * @returns Object with sparklineData map and helper methods
 *
 * @example
 * ```tsx
 * const { getData } = useClientSparklineData(matrixResult?.monthlyByClient)
 *
 * // In table cell render
 * <TableSparkline data={getData(row.codigoCliente)} />
 * ```
 */
export function useClientSparklineData(
  monthlyByClient: ClientMonthlyData[] | null | undefined
): UseClientSparklineDataReturn {
  const sparklineData = useMemo(() => {
    const map = new Map<string, number[]>()

    if (!monthlyByClient) {
      return map
    }

    for (const client of monthlyByClient) {
      // Extract just the revenue values, already sorted by period from backend
      const values = client.months.map(m => m.receita)
      map.set(client.codigoCliente, values)
    }

    return map
  }, [monthlyByClient])

  const hasData = useMemo(
    () => (codigoCliente: string): boolean => {
      const data = sparklineData.get(codigoCliente)
      return !!data && data.length > 0
    },
    [sparklineData]
  )

  const getData = useMemo(
    () => (codigoCliente: string): number[] => {
      return sparklineData.get(codigoCliente) ?? []
    },
    [sparklineData]
  )

  return {
    sparklineData,
    hasData,
    getData,
  }
}

export default useClientSparklineData
